<script setup lang="ts">
// NPM
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';

// Components
import { BaseAvatar, BaseButton, BaseIcon } from '@magma-app/magma-lapilli';

// Types
import type { PropType } from 'vue';
import type { ITouchPoint } from '@/types/ITouchPoint';

// Helpers
import AnalyticsHelper from '@/helpers/analytics';

// Stores & utils
import { useCommonStore } from '@/stores/common';
import { useRelationsStore } from '@/stores/relations';
import { useUserStore } from '@/stores/user';
import {
  copyOrOpenDiscordTouchpoint,
  isDiscordTouchpoint
} from '@/utils/discord';

const commonStore = useCommonStore();
const relationsStore = useRelationsStore();
const userStore = useUserStore();
const { t } = useI18n({ useScope: 'global' });

const props = defineProps({
  compact: { type: Boolean, default: false },
  trackContact: { type: Boolean, default: true },
  touchPoints: {
    type: Array as PropType<ITouchPoint[]>,
    default: () => []
  },
  prospectFirstname: {
    type: String,
    default: ''
  },
  welcomeMessage: {
    type: String,
    default: ''
  },
  relationMomentId: {
    type: Number,
    default: null
  },
  /** Already flagged as ongoing — don't post it again. */
  isOngoing: {
    type: Boolean,
    default: false
  },
  /** Reported or already arbitrated: channels stay visible but inert. */
  disabled: {
    type: Boolean,
    default: false
  },
  /** Hide the suggested message (e.g. once the exchange is over). */
  hideMessage: {
    type: Boolean,
    default: false
  },
  /**
   * The start-relation screen reads message-first (read it, copy it, then pick a channel); the
   * relation page reads channels-first, because by then the ambassador knows the message.
   */
  messageFirst: {
    type: Boolean,
    default: false
  },
  /** Anchors for the driver.js onboarding tour. */
  channelsClass: {
    type: String,
    default: ''
  },
  messageClass: {
    type: String,
    default: ''
  }
});

const emit = defineEmits(['contacted']);

// Reactive variables
const isCopied = ref(false);
const showAll = ref(false);
const visiblePoints = computed(() =>
  props.compact && !showAll.value
    ? props.touchPoints.slice(0, 2)
    : props.touchPoints
);
watch(
  () => props.relationMomentId,
  () => {
    showAll.value = false;
  }
);
const copyValue = async (value: string) => {
  try {
    await navigator.clipboard.writeText(value);
    commonStore.setAlert({
      open: true,
      type: 'success',
      title: t('generic.copied'),
      duration: 1800
    });
  } catch {
    commonStore.setAlert({
      open: true,
      type: 'error',
      title: t('helperRelation.channels.copyFailed')
    });
  }
};

// Computed
const hasMessage = computed(
  () => !props.hideMessage && !!props.welcomeMessage?.trim()
);

const formattedMessage = computed(() =>
  props.welcomeMessage.replace(/\n/g, '<br />')
);

// Methods
const openMailProvider = (email: string) => {
  const sanitizedEmail = email.trim();
  if (!sanitizedEmail) return;

  const mailtoUrl = sanitizedEmail.startsWith('mailto:')
    ? sanitizedEmail
    : `mailto:${sanitizedEmail}`;

  // Use _self to avoid creating a blank tab when no default mail provider is configured.
  window.open(mailtoUrl, '_self');
};

/**
 * Opening a channel is the only "contact" signal Magma ever gets — it does not mean a message was
 * sent. Flagging it here means both the relation page and the start-relation screen agree, which
 * they did not before: only the latter used to post it.
 */
const markAsOngoing = async () => {
  if (
    props.disabled ||
    !props.trackContact ||
    props.isOngoing ||
    !props.relationMomentId
  )
    return;

  try {
    await relationsStore.markRelationMomentAs({
      relationMomentId: props.relationMomentId,
      role: 'ongoing',
      type: 'helper'
    });
    emit('contacted');
  } catch (error) {
    // The server rejects a second flag with ONGOING-SHOULD-BE-NULL; the exchange is already
    // marked, so there is nothing to tell the ambassador about.
    console.warn('Could not flag the exchange as ongoing', error);
  }
};

// Event listeners
const onCopyMessage = async () => {
  if (!props.welcomeMessage) return;

  try {
    await window.navigator.clipboard.writeText(props.welcomeMessage);
  } catch {
    commonStore.setAlert({
      open: true,
      type: 'error',
      title: t('helperRelation.channels.copyFailed')
    });
    return;
  }
  isCopied.value = true;

  setTimeout(() => {
    isCopied.value = false;
  }, 2000);
};

const onTouchPointClick = async (touchPoint: ITouchPoint) => {
  if (props.disabled) return;

  if (['line', 'phone'].includes(touchPoint.name)) {
    commonStore.setCallTextModal({
      helpeeName: props.prospectFirstname,
      helperName: userStore.user?.firstname,
      open: true,
      touchpoint: touchPoint.value
    });
  } else if (touchPoint.name === 'whatsapp') {
    const phoneNumber = touchPoint.value.replace(/\D/g, '');
    window.open(`https://api.whatsapp.com/send?phone=${phoneNumber}`, '_blank');
  } else if (touchPoint.name === 'mail') {
    openMailProvider(touchPoint.value);
  } else if (touchPoint.type === 'pseudo') {
    if (isDiscordTouchpoint(touchPoint.name)) {
      let copied = false;
      try {
        copied = await copyOrOpenDiscordTouchpoint(touchPoint.value);
      } catch {
        commonStore.setAlert({
          open: true,
          type: 'error',
          title: t('helperRelation.channels.copyFailed')
        });
        return;
      }

      if (copied) {
        commonStore.setAlert({
          open: true,
          type: 'success',
          title: t('generic.copied')
        });
      }
    } else {
      if (/^https?:\/\//i.test(touchPoint.value))
        window.open(touchPoint.value, '_blank', 'noopener,noreferrer');
      else {
        await copyValue(touchPoint.value);
        return;
      }
    }
  }

  AnalyticsHelper.trackEvent('send_message', {
    event_properties: 'touchpoints',
    user_properties: 'helper'
  });

  await markAsOngoing();
};

defineExpose({ onTouchPointClick });
</script>

<template>
  <div v-if="touchPoints.length || hasMessage" class="flex flex-col gap-4">
    <!-- channels -->
    <div
      v-if="touchPoints.length"
      :class="[channelsClass, { 'order-2': messageFirst }]"
    >
      <p
        v-if="!compact"
        class="mb-2 text-mgm-txt-sm font-medium text-foreground-emphasis"
      >
        {{ $t('helperRelation.channels.title') }}
      </p>

      <div
        class="divide-y divide-border-subtle"
        :class="{
          'overflow-hidden rounded-lg border border-border-subtle': !compact
        }"
      >
        <div
          v-for="touchPoint in visiblePoints"
          :key="touchPoint.touchPointId"
          class="flex min-h-[52px] items-center gap-3 py-2"
          :class="{ 'px-3': !compact }"
          :data-cy="`relation-channel-${touchPoint.name}`"
        >
          <BaseAvatar
            :src="touchPoint.icon"
            :alt="touchPoint.name"
            size="sd"
            shape="rounded"
            class="shrink-0"
          />
          <span class="min-w-0 flex-1 text-mgm-txt-sm text-foreground-default">
            <span v-if="!compact" class="block font-medium capitalize">{{
              touchPoint.name
            }}</span>
            <span class="block truncate" :title="touchPoint.value">{{
              touchPoint.value
            }}</span>
          </span>
          <div class="flex shrink-0 items-center">
            <button
              type="button"
              class="flex h-11 w-11 shrink-0 items-center justify-center rounded-md hover:bg-background-subtle-hover focus-visible:outline focus-visible:outline-2 disabled:opacity-40"
              :disabled="disabled"
              :aria-label="
                $t('helperRelation.channels.copyValue', {
                  channel: touchPoint.name
                })
              "
              :title="
                $t('helperRelation.channels.copyValue', {
                  channel: touchPoint.name
                })
              "
              @click="copyValue(touchPoint.value)"
            >
              <BaseIcon icon="copy" :size="18" />
            </button>
            <button
              type="button"
              class="flex h-11 w-11 shrink-0 items-center justify-center rounded-md hover:bg-background-subtle-hover focus-visible:outline focus-visible:outline-2 disabled:opacity-40"
              :disabled="disabled"
              :aria-label="
                $t('helperRelation.channels.openValue', {
                  channel: touchPoint.name
                })
              "
              :title="
                $t('helperRelation.channels.openValue', {
                  channel: touchPoint.name
                })
              "
              @click="onTouchPointClick(touchPoint)"
            >
              <BaseIcon icon="arrow-up-right" :size="18" />
            </button>
          </div>
        </div>
      </div>
      <button
        v-if="compact && touchPoints.length > 2"
        type="button"
        class="mt-2 flex min-h-11 items-center gap-2 text-mgm-txt-xs text-foreground-brand-default"
        :aria-expanded="showAll"
        @click="showAll = !showAll"
      >
        {{
          showAll
            ? $t('generic.seeLess')
            : $t('helperRelation.channels.more', {
                count: touchPoints.length - 2
              })
        }}
        <BaseIcon
          icon="chevron-down"
          :size="16"
          :class="{ 'rotate-180': showAll }"
        />
      </button>
    </div>

    <!-- suggested intro message -->
    <div v-if="hasMessage" :class="[messageClass, { 'order-1': messageFirst }]">
      <div class="mb-2 flex items-center justify-between gap-2">
        <p class="text-mgm-txt-xs font-medium text-foreground-emphasis">
          {{ $t('helperRelation.channels.suggested') }}
        </p>

        <BaseButton
          state="tertiary"
          icon="copy"
          data-cy="copy-welcome-message"
          @click="onCopyMessage"
        >
          {{
            isCopied
              ? $t('helperRelation.channels.copied')
              : $t('helperRelation.channels.copy')
          }}
        </BaseButton>
      </div>

      <div
        class="scrollbar max-h-40 overflow-y-auto rounded-lg border border-border-subtle bg-background-subtle px-3 py-2"
      >
        <p
          class="text-mgm-txt-sm text-foreground-default"
          v-html="formattedMessage"
        ></p>
      </div>
    </div>
  </div>
</template>
